/**
 * Recording what a technician did to a machine, and showing the history.
 *
 * WHY IT SITS ON THE MACHINE ROW. The moment somebody knows a restore was
 * tested is the moment they finish testing it, looking at that machine. A
 * separate compliance screen visited once a quarter collects guesses.
 *
 * IT DOES NOT CHANGE THE STATUS, and the panel says so. "I fixed it" is a
 * claim about an action, not evidence of a successful backup - the next backup
 * turns the board green. Anything else would make the board a record of
 * intentions rather than of what happened.
 *
 * THE HISTORY IS SHOWN, NOT JUST THE FORM. An append-only record you cannot
 * see is one nobody trusts, and seeing "verified 3 weeks ago, passed" is
 * usually the answer somebody actually wanted.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { errText } from '../btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

type Kind = 'remediated' | 'verified' | 'restore-tested';
type Outcome = 'pass' | 'fail' | 'partial';

interface ActionRow {
  id: string;
  kind: Kind;
  outcome: Outcome | null;
  note: string;
  byEmail: string;
  performedAtMs: number;
  recordedAtMs: number;
}

const recordAction = call<{
  orgId: string; targetId: string; kind: Kind; outcome?: Outcome | null; note?: string; performedAtMs?: number;
}, { ok: boolean }>('recordAction');
const listActions = call<{ orgId: string; targetId: string }, { actions: ActionRow[] }>('listActions');

const KINDS: Array<{ id: Kind; label: string; hint: string }> = [
  { id: 'verified', label: 'Verified', hint: 'You checked the backup is good.' },
  { id: 'restore-tested', label: 'Restore tested', hint: 'You actually restored something.' },
  { id: 'remediated', label: 'Remediated', hint: 'You fixed a failure.' },
];

export function ActionLog({ orgId, targetId }: { orgId: string | null; targetId: string }) {
  const [rows, setRows] = useState<ActionRow[]>([]);
  const [open, setOpen] = useState<Kind | null>(null);
  const [outcome, setOutcome] = useState<Outcome>('pass');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (!orgId) return;
    try { setRows((await listActions({ orgId, targetId })).actions); setLoaded(true); }
    catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [orgId, targetId]);

  if (!orgId) return null;

  // A verification or a restore test has to say how it went; a remediation has
  // to say what was done. Enforced on the server too - this only stops the
  // round trip.
  const needsOutcome = open === 'verified' || open === 'restore-tested';
  const canSave = open !== null && (open !== 'remediated' || note.trim().length >= 3);

  async function save() {
    if (!open || !orgId) return;
    setBusy(true); setErr(null);
    try {
      await recordAction({
        orgId, targetId, kind: open,
        outcome: needsOutcome ? outcome : null,
        note: note.trim(),
      });
      setOpen(null); setNote(''); setOutcome('pass');
      await load();
    } catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="act">
      <div className="act-head">
        <span className="flbl">Record what you did</span>
        <span className="dim" style={{ fontSize: 11 }}>
          Kept for compliance reporting. It does not change this machine&rsquo;s status &mdash;
          the next successful backup does that.
        </span>
      </div>

      {err && <div className="banner banner-error">{err}</div>}

      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {KINDS.map((k) => (
          <button
            key={k.id} type="button" title={k.hint}
            className={'btn' + (open === k.id ? ' btn-primary' : '')}
            onClick={() => { setOpen(open === k.id ? null : k.id); setErr(null); }}
          >
            {k.label}
          </button>
        ))}
      </div>

      {open && (
        <div className="act-form">
          {needsOutcome && (
            <div className="row" style={{ gap: 6, alignItems: 'center' }}>
              <span className="field-label" style={{ margin: 0 }}>How did it go?</span>
              {(['pass', 'partial', 'fail'] as Outcome[]).map((o) => (
                <button
                  key={o} type="button"
                  className={'btn' + (outcome === o ? ' btn-primary' : '')}
                  onClick={() => setOutcome(o)}
                >{o}</button>
              ))}
            </div>
          )}
          <label className="field" style={{ marginTop: 8 }}>
            <span className="field-label">
              {open === 'remediated' ? 'What did you do?' : 'Note (optional)'}
            </span>
            <input
              className="field-input" value={note} autoFocus
              placeholder={open === 'remediated' ? 'Cleared the full volume and reran the job' : ''}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <div className="row" style={{ gap: 8, marginTop: 8 }}>
            <button className="btn btn-primary" disabled={busy || !canSave} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Record it'}
            </button>
            <button className="btn" disabled={busy} onClick={() => { setOpen(null); setNote(''); }}>
              Cancel
            </button>
            {!canSave && open === 'remediated' && (
              <span className="dim" style={{ fontSize: 11, alignSelf: 'center' }}>
                Say what you did &mdash; a remediation with no note is not evidence of one.
              </span>
            )}
          </div>
        </div>
      )}

      {loaded && rows.length > 0 && (
        <table className="mini-table" style={{ marginTop: 10 }}>
          <thead><tr><th>When</th><th>What</th><th>Result</th><th>By</th><th>Note</th></tr></thead>
          <tbody>
            {rows.slice(0, 8).map((r) => (
              <tr key={r.id}>
                <td className="mono" style={{ fontSize: 11 }}>
                  {new Date(r.performedAtMs).toLocaleDateString()}
                </td>
                <td>{r.kind}</td>
                <td>
                  {r.outcome
                    ? <span className={'tag' + (r.outcome === 'fail' ? ' tag-bad' : r.outcome === 'pass' ? ' tag-ok' : ' tag-warn')}>
                        {r.outcome}
                      </span>
                    : ''}
                </td>
                <td className="dim" style={{ fontSize: 11 }}>{r.byEmail}</td>
                <td className="dim" style={{ fontSize: 11 }}>{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {loaded && rows.length === 0 && (
        <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>
          Nothing recorded against this machine yet.
        </div>
      )}
    </div>
  );
}
